import React, { useState, useEffect } from 'react';
import { Col, Row, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from 'reactstrap';
import Flatpickr from "react-flatpickr";

const Section = ({ rightClickBtn, dateRange, onDateRangeChange, selectedGroup, onGroupChange, connectionStatus, onRequestData, waitingTime }) => {
    const [groupByDropdown, setGroupByDropdown] = useState(false);
    const [username, setUsername] = useState('User');

    useEffect(() => {
        // Get username from session storage
        try {
            const authUser = JSON.parse(sessionStorage.getItem('authUser'));
            if (authUser && authUser.username) {
                setUsername(authUser.username);
            } else if (authUser && authUser.name) {
                setUsername(authUser.name);
            } else if (authUser && authUser.email) {
                // Use the part before @ in email if no username/name is available
                setUsername(authUser.email.split('@')[0]);
            }
        } catch (error) {
            console.error('Error getting username from session storage:', error);
        }
    }, []);

    useEffect(() => {
        // Add CSS for spin animation
        const style = document.createElement('style');
        style.innerHTML = `
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
            .spin {
                animation: spin 1.5s linear infinite;
            }
            .websocket-status-icon {
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;
            }
            .websocket-status-icon:hover {
                transform: scale(1.15);
                box-shadow: 0 0 10px rgba(0,0,0,0.2);
            }
            .websocket-status-icon:active {
                transform: scale(0.95);
            }
            
            @keyframes pulse-light {
                0% { box-shadow: 0 0 0 0 rgba(108, 117, 125, 0.4); }
                70% { box-shadow: 0 0 0 8px rgba(108, 117, 125, 0); }
                100% { box-shadow: 0 0 0 0 rgba(108, 117, 125, 0); }
            }
            
            @keyframes pulse-info {
                0% { box-shadow: 0 0 0 0 rgba(13, 202, 240, 0.4); }
                70% { box-shadow: 0 0 0 8px rgba(13, 202, 240, 0); }
                100% { box-shadow: 0 0 0 0 rgba(13, 202, 240, 0); }
            }
            
            @keyframes pulse-success {
                0% { box-shadow: 0 0 0 0 rgba(25, 135, 84, 0.4); }
                70% { box-shadow: 0 0 0 8px rgba(25, 135, 84, 0); }
                100% { box-shadow: 0 0 0 0 rgba(25, 135, 84, 0); }
            }
            
            @keyframes pulse-warning {
                0% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4); }
                70% { box-shadow: 0 0 0 8px rgba(255, 193, 7, 0); }
                100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
            }
            
            @keyframes pulse-danger {
                0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4); }
                70% { box-shadow: 0 0 0 8px rgba(220, 53, 69, 0); }
                100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
            }
            
            @keyframes float {
                0% { transform: translateY(0px); }
                50% { transform: translateY(-3px); }
                100% { transform: translateY(0px); }
            }
            
            @keyframes glow {
                0% { filter: brightness(1); }
                50% { filter: brightness(1.2); }
                100% { filter: brightness(1); }
            }
            
            .status-connected i,
            .status-authenticated i,
            .status-subscribed i {
                animation: float 3s ease-in-out infinite, glow 2s ease-in-out infinite;
            }
            
            .status-waiting-for-updates {
                animation: pulse-info 2s infinite !important;
            }
            
            .status-connected,
            .status-authenticated,
            .status-subscribed {
                animation: pulse-success 3s infinite !important;
            }
            
            .status-error,
            .status-closed {
                animation: pulse-danger 2s infinite !important;
            }
            
            .status-disconnected {
                animation: pulse-warning 2s infinite !important;
            }
            
            .status-dot {
                position: absolute;
                bottom: 2px;
                right: 2px;
                width: 8px;
                height: 8px;
                border-radius: 50%;
                z-index: 2;
            }
            
            .websocket-status-icon::after {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: radial-gradient(circle at center, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%);
                opacity: 0.4;
                z-index: 1;
                pointer-events: none;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            // Remove the style element
            document.head.removeChild(style);
        };
    }, []);

    return (
        <React.Fragment>
            <Row className="mb-3 pb-1">
                <Col xs={12}>
                    <div className="d-flex align-items-lg-center flex-lg-row flex-column">
                        <div className="flex-grow-1">
                            <h4 className="fs-16 mb-1">Good Day, {username}!</h4>
                            <p className="text-muted mb-0">Here's what's happening with Koottu today.</p>
                        </div>
                        <div className="mt-3 mt-lg-0">
                            <form action="#">
                                <Row className="g-3 mb-0 align-items-center">
                                    <div className="col-sm-auto">
                                        <div className="input-group">
                                            <Flatpickr
                                                className="form-control border-0 fs-13 dash-filter-picker shadow"
                                                options={{
                                                    mode: "range",
                                                    dateFormat: "d M, Y",
                                                    defaultDate: [dateRange.start, dateRange.end],
                                                    onChange: onDateRangeChange
                                                }}
                                            />
                                            <div className="input-group-text bg-secondary border-secondary text-white">
                                                <i className="ri-calendar-2-line"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-auto">
                                        <div className="input-group">
                                            <Dropdown isOpen={groupByDropdown} toggle={() => setGroupByDropdown(!groupByDropdown)}>
                                                <DropdownToggle tag="button" className="btn btn-soft-secondary fs-13 d-flex align-items-center" style={{ height: '36px', padding: '0.45rem 0.9rem' }}>
                                                    <i className="ri-group-line align-middle me-1"></i> {selectedGroup.charAt(0).toUpperCase() + selectedGroup.slice(1)} <i className="ri-arrow-down-s-line align-middle ms-1"></i>
                                                </DropdownToggle>
                                                <DropdownMenu className="dropdown-menu-end">
                                                    <DropdownItem onClick={() => onGroupChange('daily')}><i className="ri-calendar-line align-middle me-1"></i> Daily</DropdownItem>
                                                    <DropdownItem onClick={() => onGroupChange('weekly')}><i className="ri-calendar-check-line align-middle me-1"></i> Weekly</DropdownItem>
                                                    <DropdownItem onClick={() => onGroupChange('monthly')}><i className="ri-calendar-2-line align-middle me-1"></i> Monthly</DropdownItem>
                                                    <DropdownItem onClick={() => onGroupChange('yearly')}><i className="ri-calendar-todo-line align-middle me-1"></i> Yearly</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </div>
                                    </div>
                                    {/* WebSocket Status Icon */}
                                    <div className="col-auto">
                                        {connectionStatus && (
                                            <div 
                                                id="websocket-status-icon"
                                                className={`websocket-status-icon ms-2 status-${connectionStatus.replace(/\s+/g, '-')}`}
                                                onClick={onRequestData}
                                                style={{
                                                    cursor: 'pointer',
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: 
                                                        connectionStatus === 'connecting' ? 'linear-gradient(135deg, #f8f9fa, #e9ecef)' :
                                                        connectionStatus === 'connected' || connectionStatus === 'authenticated' || connectionStatus === 'subscribed' ? 'linear-gradient(135deg, #d1e7dd, #a3cfbb)' :
                                                        connectionStatus === 'waiting for updates' ? 'linear-gradient(135deg, #cff4fc, #9eeaf9)' :
                                                        connectionStatus === 'error' || connectionStatus === 'closed' ? 'linear-gradient(135deg, #f8d7da, #f5c2c7)' :
                                                        'linear-gradient(135deg, #fff3cd, #ffe69c)',
                                                    border: '2px solid',
                                                    borderColor:
                                                        connectionStatus === 'connecting' ? '#dee2e6' :
                                                        connectionStatus === 'connected' || connectionStatus === 'authenticated' || connectionStatus === 'subscribed' ? '#badbcc' :
                                                        connectionStatus === 'waiting for updates' ? '#b6effb' :
                                                        connectionStatus === 'error' || connectionStatus === 'closed' ? '#f5c2c7' :
                                                        '#ffecb5',
                                                    boxShadow: 
                                                        connectionStatus === 'connecting' ? '0 2px 5px rgba(0,0,0,0.1)' :
                                                        connectionStatus === 'connected' || connectionStatus === 'authenticated' || connectionStatus === 'subscribed' ? '0 2px 8px rgba(25, 135, 84, 0.25), 0 0 12px rgba(25, 135, 84, 0.2)' :
                                                        connectionStatus === 'waiting for updates' ? '0 2px 8px rgba(13, 202, 240, 0.25), 0 0 12px rgba(13, 202, 240, 0.2)' :
                                                        connectionStatus === 'error' || connectionStatus === 'closed' ? '0 2px 8px rgba(220, 53, 69, 0.25), 0 0 12px rgba(220, 53, 69, 0.2)' :
                                                        '0 2px 8px rgba(255, 193, 7, 0.25), 0 0 12px rgba(255, 193, 7, 0.2)'
                                                }}>
                                                {connectionStatus === 'connecting' ? (
                                                    <i className="ri-loader-4-line spin" style={{ 
                                                        color: '#6c757d', 
                                                        fontSize: '16px',
                                                        filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                                                        position: 'relative',
                                                        zIndex: 2
                                                    }}></i>
                                                ) : connectionStatus === 'connected' || connectionStatus === 'authenticated' || connectionStatus === 'subscribed' ? (
                                                    <i className="ri-wifi-line" style={{ 
                                                        color: '#198754', 
                                                        fontSize: '16px',
                                                        filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                                                        position: 'relative',
                                                        zIndex: 2
                                                    }}></i>
                                                ) : connectionStatus === 'waiting for updates' ? (
                                                    <i className="ri-refresh-line spin" style={{ 
                                                        color: '#0dcaf0', 
                                                        fontSize: '16px',
                                                        filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                                                        position: 'relative',
                                                        zIndex: 2
                                                    }}></i>
                                                ) : connectionStatus === 'error' ? (
                                                    <i className="ri-error-warning-line" style={{ 
                                                        color: '#dc3545', 
                                                        fontSize: '16px',
                                                        filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                                                        position: 'relative',
                                                        zIndex: 2
                                                    }}></i>
                                                ) : connectionStatus === 'closed' ? (
                                                    <i className="ri-plug-line" style={{ 
                                                        color: '#dc3545', 
                                                        fontSize: '16px',
                                                        filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                                                        position: 'relative',
                                                        zIndex: 2
                                                    }}></i>
                                                ) : (
                                                    <i className="ri-wifi-off-line" style={{ 
                                                        color: '#ffc107', 
                                                        fontSize: '16px',
                                                        filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
                                                        position: 'relative',
                                                        zIndex: 2
                                                    }}></i>
                                                )}
                                                
                                                {/* Small dot indicator */}
                                                {(connectionStatus === 'connected' || connectionStatus === 'authenticated' || connectionStatus === 'subscribed') && (
                                                    <span className="status-dot" style={{
                                                        background: '#198754',
                                                        border: '1px solid #badbcc',
                                                        boxShadow: '0 0 4px rgba(25, 135, 84, 0.5)',
                                                        animation: 'pulse-success 2s infinite'
                                                    }}></span>
                                                )}
                                                
                                                {connectionStatus === 'waiting for updates' && (
                                                    <span className="status-dot" style={{
                                                        background: '#0dcaf0',
                                                        border: '1px solid #b6effb',
                                                        boxShadow: '0 0 4px rgba(13, 202, 240, 0.5)',
                                                        animation: 'pulse-info 2s infinite'
                                                    }}></span>
                                                )}
                                                
                                                {(connectionStatus === 'error' || connectionStatus === 'closed') && (
                                                    <span className="status-dot" style={{
                                                        background: '#dc3545',
                                                        border: '1px solid #f5c2c7',
                                                        boxShadow: '0 0 4px rgba(220, 53, 69, 0.5)',
                                                        animation: 'pulse-danger 2s infinite'
                                                    }}></span>
                                                )}
                                                
                                                <UncontrolledTooltip placement="top" target="websocket-status-icon">
                                                    {connectionStatus === 'connecting' ? 'Connecting to real-time updates...' :
                                                    connectionStatus === 'connected' || connectionStatus === 'authenticated' ? 'Connected to real-time updates' :
                                                    connectionStatus === 'subscribed' ? 'Receiving real-time updates' :
                                                    connectionStatus === 'waiting for updates' ? `Waiting for updates (${waitingTime}s)...` :
                                                    connectionStatus === 'error' ? 'Connection error. Click to retry.' :
                                                    connectionStatus === 'closed' ? 'Connection closed. Click to reconnect.' :
                                                    'Disconnected. Click to connect.'}
                                                </UncontrolledTooltip>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-auto">
                                        <button type="button" className="btn btn-soft-info btn-icon waves-effect waves-light layout-rightside-btn" style={{ height: '36px', width: '36px' }} onClick={rightClickBtn} ><i className="ri-pulse-line"></i></button>
                                    </div>
                                </Row>
                            </form>
                        </div>
                    </div>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default Section;